import { createSelector } from '@reduxjs/toolkit'
import { DataStatus } from '@repo/ui/utils'
import { filter } from 'lodash'

import { RootState } from '@/store'
import { FilterAndSchemaItemList } from '@/store/beneficiary-list-filters/types'
import { getCurrentOrganization } from '@/store/helpers'

const selectViewState = (state: RootState) => state.beneficiaryList

export const selectCurrentFilterTemplate = createSelector(
  selectViewState,
  (state) => state.currentFilterTemplate,
)

export const selectBeneficiaries = createSelector(selectViewState, (state) => state.beneficiaries)

export const selectCurrentFilters = createSelector(
  selectViewState,
  (state) => state.currentFilterTemplate.filters,
)

export const selectSavedFilterTemplates = createSelector(
  selectViewState,
  (state) => state.savedFilterTemplates,
)

export const selectCurrentSchema = createSelector([getCurrentOrganization], (organization) => {
  if (!DataStatus.isFulfilled(organization)) return []
  return organization.structuredDataSchema
})

// You can't add a new filter for a schema item that already has a filter
export const selectAvailableSchemaItems = createSelector(
  [selectCurrentFilters, getCurrentOrganization],
  (filters, organization) => {
    if (!DataStatus.isFulfilled(organization)) return []
    const structuredDataSchema = organization.structuredDataSchema
    return structuredDataSchema.filter(
      (schemaItem) => !filters.some((filter) => filter.key === schemaItem.id),
    )
  },
)

// we need to filter out filters that are not in the structured data schema (in case the schema has changed)
export const selectFilterAndSchemaItemList = createSelector(
  [selectCurrentFilters, getCurrentOrganization],
  (filters, organization): FilterAndSchemaItemList => {
    if (!DataStatus.isFulfilled(organization)) return []
    const structuredDataSchema = organization.structuredDataSchema
    return filters
      .map((filter) => {
        const schemaItem = structuredDataSchema.find((item) => item.id === filter.key)
        if (!schemaItem) return null
        return { filter, schemaItem }
      })
      .filter((item) => !!item)
  },
)

export const selectSearchText = createSelector(
  selectCurrentFilters,
  (filters) =>
    filters.find((filter) => filter.key === 'content' && filter.type === 'fullText')?.value || '',
)

export const selectIsEmpty = createSelector(selectCurrentFilters, (filters) => {
  const hasSearchText = filters.some(
    (filter) =>
      filter.key === 'content' && filter.type === 'fullText' && filter.value.trim() !== '',
  )
  const otherFilters = filter(
    filters,
    (filter) => !(filter.key === 'content' && filter.type === 'fullText'),
  )
  return !hasSearchText && otherFilters.length === 0
})
